<template>
    <div class="flex flex-row h-full p-2 gap-2">
        <div class="w-1/3 shrink-0 flex flex-col">
            <ag-grid-vue
                class="grow"
                :theme="agTheme"
                :column-defs="columns"
                :row-data="documents"
                :grid-options="gridOptions"
                row-selection="single"
                tabindex="0"
                @selection-changed="onSelectionChanged"
                @first-data-rendered="focusFirstAGCell"
            />
        </div>
        <div class="grow border rounded-lg bg-base-100 overflow-hidden">
            <ehf-raw-view
                v-if="shouldShowEhfParsedPreview"
                :incoming-document="selectedDocument!"
            />
            <document-preview
                v-if="selectedDocumentUuid"
                :document-meta-uuid="selectedDocumentUuid"
                class="h-full w-full"
            />
            <div v-else class="h-full w-full flex items-center justify-center text-gray-500">
                Velg et dokument for å se forhåndsvisning
            </div>
        </div>
    </div>
    <UModal v-model:open="showDeleteConfirmation" title="Slett dokument?">
        <template #body>
            <p>Er du sikker på at du vil slette det valgte dokumentet? Handlingen kan ikke angres.</p>
        </template>
        <template #footer>
            <div class="flex justify-end gap-2 w-full">
                <UButton color="neutral" variant="ghost" @click="showDeleteConfirmation = false">Avbryt</UButton>
                <UButton color="error" @click="deleteSelected">Slett</UButton>
            </div>
        </template>
    </UModal>
</template>

<script setup lang="ts">
import {AgGridVue} from 'ag-grid-vue3'
import {agTheme} from '@/agUtils/ag-theme.service'
import {INCOMING_DOCUMENT_EVENTS, listIncomingDocumentsV2, approveIncomingDocument, documentMetaDelete} from '@/services/document.service'
import {ATTACHMENT_UPLOADED, ORG_SWITCHED} from '@/types/events'
import {LIST_DOCUMENTS} from '@/task-manager/task.events'
import {computed, h, onMounted, onUnmounted, ref} from 'vue'
import emitter, {useEmitter} from '@/services/infrastructure/emitter'
import {IncomingDocument} from '@/types/IncomingDocument'
import {ColDef, GridApi, ICellRendererParams, ValueGetterParams, CellFocusedEvent, IRowNode, GridReadyEvent} from 'ag-grid-community'
import TaskNameCell from '@/task-manager/TaskNameCell.vue'
import IncomingDocumentApprovalButtons from '@/task-manager/IncomingDocumentApprovalButtons.vue'
import {useMagicKeys, whenever} from '@vueuse/core'
import DocumentPreview from '@/document/DocumentPreview.vue'
import EhfRawView from '@/document/EhfRawView.vue'
import {PENDING} from '@/task-manager/IncomingTaskStatus'

const {cleanUp, onEvents} = useEmitter()

const selectedDocumentUuid = ref<string | null>(null)
const selectedDocument = computed(() => {
    return documents.value.find(doc => doc.documentMeta.uuid === selectedDocumentUuid.value)
})

const shouldShowEhfParsedPreview = computed(() => {
    return selectedDocument.value?.invoiceDraft && selectedDocument.value.documentMeta.contentType === 'application/json'
})

const gridApi = ref<GridApi>()

const columns: ColDef[] = [
    {
        colId: 'name',
        headerName: 'Navn',
        cellRendererSelector: ({data}: ICellRendererParams) => {
            if (data.invoiceDraft.draft.sender?.name) {
                return h('div', 'Faktura fra ' + data.invoiceDraft.draft.sender.name)
            }
            return {component: TaskNameCell}
        },
        valueGetter: ({data}: ValueGetterParams) => {
            if (data.invoiceDraft.draft.sender?.name) {
                return 'Faktura fra ' + data.invoiceDraft.draft.sender.name
            }
            return data.documentMeta.filename
        },
        flex: 1
    },
    {
        headerClass: 'hidden-header',
        cellRenderer: IncomingDocumentApprovalButtons,
        cellRendererParams: (params: any) => ({
            documentUuid: params.data.documentMeta.uuid
        }),
        width: 90,
        colId: 'documentButtons'
    }
]
const documents = ref<IncomingDocument[]>([])
const gridOptions = {
    onGridReady: (event: GridReadyEvent) => {
        gridApi.value = event.api
    },
    suppressContextMenu: true,
    onCellFocused: (event: CellFocusedEvent) => {
        if (event.rowIndex === null) return
        const row: IRowNode<IncomingDocument> | undefined = event.api.getDisplayedRowAtIndex(event.rowIndex)
        if (!row) return
        event.api.setNodesSelected({nodes: [row], newValue: true})
    }
}

const focusFirstAGCell = () => gridApi.value?.setFocusedCell(0, 'name')

const onSelectionChanged = (event: any) => {
    gridApi.value = event.api
    const selectedRows = event.api.getSelectedRows()
    if (selectedRows.length > 0) {
        selectedDocumentUuid.value = selectedRows[0].documentMeta.uuid
    } else {
        selectedDocumentUuid.value = null
    }
}

const refreshFiles = async () => {
    documents.value = (await listIncomingDocumentsV2(PENDING))
    
    if (documents.value.length > 0 && !selectedDocumentUuid.value && gridApi.value) {
        const firstRow = gridApi.value.getDisplayedRowAtIndex(0)
        if (!firstRow) return
        gridApi.value.setNodesSelected({nodes: [firstRow], newValue: true})
    }
}

const selectRowAtIndex = (index: number) => {
    if (!gridApi.value) return
    const rowCount = gridApi.value.getDisplayedRowCount()
    if (rowCount === 0) return
    
    // Ensure index is valid
    const targetIndex = Math.min(index, rowCount - 1)
    if (targetIndex < 0) return
    
    const node = gridApi.value.getDisplayedRowAtIndex(targetIndex)
    if (node) {
        node.setSelected(true)
        gridApi.value.ensureIndexVisible(targetIndex)
    }
}

const getCurrentRowIndex = (): number => {
    const selectedNodes = gridApi.value?.getSelectedNodes()
    if (selectedNodes && selectedNodes.length > 0) {
        return selectedNodes[0].rowIndex ?? 0
    }
    return 0
}

const approveSelected = async () => {
    if (!selectedDocumentUuid.value) return
    const currentIndex = getCurrentRowIndex()
    await approveIncomingDocument(selectedDocumentUuid.value)
    emitter.emit(LIST_DOCUMENTS)

    selectRowAtIndex(currentIndex)
}

const deleteSelected = async () => {
    if (!selectedDocumentUuid.value) return
    const currentIndex = getCurrentRowIndex()
    await documentMetaDelete(selectedDocumentUuid.value)
    emitter.emit(LIST_DOCUMENTS)
    showDeleteConfirmation.value = false
    selectRowAtIndex(currentIndex)
}

const showDeleteConfirmation = ref(false)
const {a, delete: deleteKey, enter, escape} = useMagicKeys()

const isInputActive = () => {
    const active = document.activeElement
    if (!active) return false
    return ['INPUT', 'TEXTAREA'].includes(active.tagName) || (active as HTMLElement).isContentEditable
}

whenever(a, () => {
    if (isInputActive() || showDeleteConfirmation.value) return
    approveSelected()
})

whenever(deleteKey, () => {
    if (isInputActive() || showDeleteConfirmation.value) return
    if (selectedDocumentUuid.value) {
        showDeleteConfirmation.value = true
    }
})

whenever(enter, () => {
    if (showDeleteConfirmation.value) {
        deleteSelected()
    }
})

whenever(escape, () => {
    showDeleteConfirmation.value = false
})

onMounted(() => {
    refreshFiles()
    onEvents([...INCOMING_DOCUMENT_EVENTS, ATTACHMENT_UPLOADED, ORG_SWITCHED, LIST_DOCUMENTS], refreshFiles)
})

onUnmounted(cleanUp)
</script>

<style scoped>

</style>